/**
 * Existing tenant alterations (migration 024).
 *
 * A tenant who is already open wants a new sign or a remodel. The Property
 * Manager opens the project, uploads the sign package or the plans and the
 * tenant's certificate of insurance; the Construction Manager reviews it,
 * approves it once the insurance is accepted, and closes it out.
 *
 * The rules themselves are the API's - `createRelease` and the insurance
 * condition in api/src/lib/repo.ts. What is here only decides what the
 * screens show, so a Property Manager is told before they press a button that
 * would be refused.
 */

export const ALTERATION_TYPE = 'tenant_alteration';

/**
 * A tenant's plans, reviewed the tenant way: a new tenant's build-out and an
 * existing tenant's alteration both have a Signage tab, and both send the
 * decision straight to the Property Manager and APM. `isTenantKind` in
 * api/src/domain/notifications.ts is the same test.
 */
export function isTenantKind(type) {
  return type === 'tenant' || type === ALTERATION_TYPE;
}

/**
 * Does this kind of project get the Milestones and Timeline tabs? Not an
 * alteration: it is a review, not a build-out, is opened with no standard
 * milestones, and has nothing to put on a schedule (Evan, 2026-10-19). Tasks
 * stay, because a Revise and Resubmit raises an upload task for the Property
 * Manager; Activity stays, because it is the record of who did what.
 */
export function hasScheduleTabs(type) {
  return type !== ALTERATION_TYPE;
}

/** Is this project an alteration still waiting on the tenant's insurance? */
export function insurancePending(project) {
  return project?.type === ALTERATION_TYPE && !project?.insurance_approved_date;
}

export const INSURANCE_PENDING_MESSAGE =
  "The tenant's certificate of insurance has not been accepted yet. Nothing on this project "
  + 'can be approved until it is - you can still ask for revisions.';

/**
 * A number for a newly opened alteration: ALT-YYMMDD-NNNN. The Property
 * Manager is not asked for one - Westwood's numbering is the Construction
 * Manager's - and the database needs one. They can change it afterwards.
 */
export function alterationProjectNumber(now = new Date(), random = Math.random) {
  const pad = n => String(n).padStart(2, '0');
  const day = `${pad(now.getFullYear() % 100)}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const tail = String(Math.floor(random() * 10000)).padStart(4, '0');
  return `ALT-${day}-${tail}`;
}
